import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Upload, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BulkImport({ onDone }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/reports/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mau_danh_sach_den.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Không tải được file mẫu");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/reports/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) toast.success(`Đã thêm ${data.created} khách vào danh sách đen`);
      if (data.errors?.length) toast.warning(`${data.errors.length} dòng bị bỏ qua do lỗi`);
      if (data.created === 0 && !data.errors?.length) toast.info("Không có dòng dữ liệu nào trong file");
      onDone && onDone(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="bulk-import" className="bg-card border border-zinc-800 rounded-sm p-6">
      <h2 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-2">
        <FileSpreadsheet className="w-5 h-5 text-primary" /> Nhập hàng loạt qua Excel (.xlsx)
      </h2>
      <p className="text-zinc-500 text-sm mb-4">
        Tải file mẫu, điền thông tin khách theo đúng cột rồi tải lên. Các dòng hợp lệ sẽ được thêm & duyệt ngay.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          data-testid="download-template-btn"
          onClick={downloadTemplate}
          className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm px-4 py-2.5 text-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Tải file mẫu
        </button>
        <button
          type="button"
          data-testid="upload-xlsx-btn"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="flex items-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold rounded-sm px-4 py-2.5 text-sm transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Tải file lên
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>

      {result && (
        <div data-testid="bulk-result" className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle2 className="w-4 h-4" /> Đã thêm thành công {result.created}/{result.total_rows} dòng.
          </div>
          {result.errors?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3">
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-1">
                <AlertTriangle className="w-4 h-4" /> {result.errors.length} dòng bị bỏ qua:
              </div>
              <ul className="text-xs text-red-300 space-y-0.5 max-h-40 overflow-y-auto list-disc pl-5">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
